"use client";

/**
 * Inter-step delay pill (Figma `10356:11004`) — the small rounded chip
 * shown on the connector line BETWEEN two step cards in the right pane.
 *
 * The label is derived entirely from the *next* step's own schedule
 * fields on the pinned `SequenceStepResponse` (`schedule_mode`,
 * `schedule_delay_value`, `schedule_delay_type`, `schedule_at_time`) — no
 * invented backend data. The wording follows the outreach scheduling
 * model:
 *   - automatic email: the schedule is WHEN TO SEND → "Wait 2 business days".
 *   - manual tasks: the task is created immediately when the prior step
 *     completes; the schedule is the DUE DATE → "Schedule immediately ·
 *     due in 2 business days".
 *
 * Read-only: per-step timing is a property of the sequence, not the
 * enrollment, so the pill is a label (the chevron is decorative, matching
 * the Figma).
 */

import { Icon } from "@/components/ui/icon";
import {
  STEP_TYPE_META,
  delayUnitLabel,
  formatTimeOfDay,
  type ScheduleDelayUnit,
  type StepType,
} from "@/features/sequences/lib/step-type-meta";
import type { SequenceStepResponse } from "@/lib/api/model";

export interface DelayPillProps {
  /** The step that follows the gap — its schedule defines the delay. */
  nextStep: SequenceStepResponse;
}

function delayLabel(step: SequenceStepResponse): string {
  const meta = STEP_TYPE_META[step.type as StepType];
  const isAutomatic = meta ? !meta.is_manual_task : false;
  const mode = step.schedule_mode;

  if (mode === "delay") {
    const n = step.schedule_delay_value ?? 1;
    const unit = delayUnitLabel(
      (step.schedule_delay_type ?? "business_days") as ScheduleDelayUnit,
      n,
    );
    if (isAutomatic) return `Wait ${n} ${unit}`;
    return `Schedule immediately · due in ${n} ${unit}`;
  }

  if (mode === "soonest_at") {
    return `Soonest at ${formatTimeOfDay(step.schedule_at_time ?? null)}`;
  }

  // immediate
  return isAutomatic
    ? "Immediately after previous step"
    : "Schedule immediately";
}

export function DelayPill({ nextStep }: DelayPillProps) {
  // Render like a React Flow edge: ONE continuous vertical connector line
  // running card-to-card, with the label chip centered ON it (the chip's
  // opaque background masks the line behind it). The negative vertical margin
  // cancels the parent column's `gap-5` (20px) on both sides so the line
  // touches the cards above and below instead of floating with a gap.
  return (
    <div className="relative -my-5 flex min-h-[64px] w-full items-center justify-center">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-white/15"
      />
      <div className="relative inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-surface-elevated px-3 py-1 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
        <Icon name="IconCalendar1" size={14} className="text-foreground opacity-70" />
        <span className="whitespace-nowrap text-sm font-medium text-heading">
          {delayLabel(nextStep)}
        </span>
      </div>
    </div>
  );
}
